import React, {
    ChangeEvent,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import type { Element } from '../ChipSelector';
import { sortElementsByName } from '../utils';
import ElementListItem from '../ElementListItem/ElementListItem';
import styles from './ElementDropdown.module.scss';

interface Props<T> {
    id?: string;
    elements: Array<Element<T>>;
    searchLabel: string;
    allLabel: string;
    placement: 'top' | 'bottom';
    anchorRef: RefObject<HTMLElement>;
    onSelect: (elements: Array<Element<T>>) => void;
    onClose: () => void;
}

interface DropdownPosition {
    top: number;
    left: number;
}

function matchElement<T>(element: Element<T>, searchText: string): boolean {
    const searchTokens = searchText
        .split(' ')
        .map(token => token.trim().toLowerCase())
        .filter(Boolean);

    if (searchTokens.length === 0) {
        return true;
    }

    const nameWords = element.name
        .split(' ')
        .map(word => word.trim().toLowerCase())
        .filter(Boolean);

    return searchTokens.some(searchToken => nameWords.some(nameWord => nameWord.includes(searchToken)));
}

export function ElementDropdown<T extends string | number>(props: Props<T>): React.ReactElement | null {
    const containerRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(props.onClose);
    const [searchText, setSearchText] = useState('');
    const [position, setPosition] = useState<DropdownPosition | null>(null);

    onCloseRef.current = props.onClose;

    const sortedElements = useMemo(() => sortElementsByName(props.elements), [props.elements]);

    const filteredElements = useMemo(
        () => sortedElements.filter(element => matchElement(element, searchText)),
        [sortedElements, searchText]
    );

    const calculatePosition = useCallback(() => {
        if (!props.anchorRef.current || !containerRef.current) {
            return;
        }

        const triggerRect = props.anchorRef.current.getBoundingClientRect();
        const contentRect = containerRef.current.getBoundingClientRect();

        const top = props.placement === 'top' ? triggerRect.bottom - contentRect.height : triggerRect.top;

        setPosition({
            top,
            left: triggerRect.left
        });
    }, [props.anchorRef, props.placement]);

    useEffect(() => {
        setSearchText('');
    }, [props.elements]);

    useLayoutEffect(() => {
        calculatePosition();
    }, [calculatePosition, filteredElements.length]);

    useEffect(() => {
        const handleScrollOrResize = () => {
            calculatePosition();
        };

        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [calculatePosition]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (containerRef.current?.contains(target)) {
                return;
            }

            if (props.anchorRef.current?.contains(target)) {
                return;
            }

            onCloseRef.current();
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props.anchorRef]);

    const changeSearchTextHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const selectHandler = (elementId: T) => {
        const selectedElement = props.elements.find(element => element.id === elementId);

        if (selectedElement) {
            props.onSelect([selectedElement]);
        }
    };

    const selectAllHandler = () => {
        props.onSelect([...sortedElements]);
    };

    const showImages = props.elements.some(element => Boolean(element.img));

    const listItems = filteredElements.map((element, index) => (
        <ElementListItem
            key={String(element.id)}
            id={props.id ? `${props.id}_list_${index}` : undefined}
            name={element.name}
            img={element.img}
            showImage={showImages}
            isFirst={index === 0}
            isLast={index === filteredElements.length - 1}
            onSelect={() => selectHandler(element.id)}
        />
    ));

    return createPortal(
        <div
            ref={containerRef}
            className={styles.container}
            style={{
                top: position?.top,
                left: position?.left,
                visibility: position ? 'visible' : 'hidden'
            }}
        >
            <div className={styles.dropdown}>
                <div className={styles.header}>
                    {showImages && <span className={styles.searchPlaceholder} />}
                    <input
                        type="text"
                        autoFocus
                        className={styles.searchInput}
                        placeholder={props.searchLabel}
                        value={searchText}
                        onChange={changeSearchTextHandler}
                    />
                </div>
                <div className={styles.scroll}>
                    <div
                        id={props.id ? `${props.id}_list` : undefined}
                        className={styles.body}
                    >
                        <ElementListItem
                            id={props.id ? `${props.id}_list_all` : undefined}
                            name={props.allLabel}
                            showImage={showImages}
                            onSelect={selectAllHandler}
                        />
                        {listItems}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default ElementDropdown;
